/**
 * Presentation-mode metadata for CirclePlaydates (Phase 3.2H).
 *
 * Stored in CirclePlaydate.note as a leading control envelope so we avoid
 * schema migrations. User-visible note text is separated from the envelope.
 *
 * Daypart plans use representative scheduledStartAt hours; exact plans use the
 * true local clock. Formatters must read `mode` — never infer exact UI from
 * daypart representative instants alone.
 */

export type CirclePlaydatePresentationMode = 'daypart' | 'exact'

const ENVELOPE_PREFIX = '\u001Ewag.plan.v1:'

export type DecodedCirclePlaydateNote = {
  presentationMode: CirclePlaydatePresentationMode
  userNote: string | null
}

export function encodeCirclePlaydateNote(input: {
  presentationMode: CirclePlaydatePresentationMode
  userNote?: string | null
}): string | null {
  const user = input.userNote?.trim() || ''
  const meta = `${ENVELOPE_PREFIX}${JSON.stringify({
    m: input.presentationMode === 'exact' ? 'e' : 'd',
  })}`
  if (!user) {
    // Persist mode even with empty user note so exact vs daypart survives.
    return meta
  }
  return `${meta}\n${user}`
}

export function decodeCirclePlaydateNote(
  raw: string | null | undefined
): DecodedCirclePlaydateNote {
  if (raw == null || raw === '') {
    return { presentationMode: 'daypart', userNote: null }
  }
  if (!raw.startsWith(ENVELOPE_PREFIX)) {
    return { presentationMode: 'daypart', userNote: raw }
  }
  const rest = raw.slice(ENVELOPE_PREFIX.length)
  const nl = rest.indexOf('\n')
  const jsonPart = nl === -1 ? rest : rest.slice(0, nl)
  const userPart = nl === -1 ? '' : rest.slice(nl + 1)
  try {
    const parsed = JSON.parse(jsonPart) as { m?: string }
    const presentationMode: CirclePlaydatePresentationMode =
      parsed.m === 'e' ? 'exact' : 'daypart'
    const userNote = userPart.trim() ? userPart.trim() : null
    return { presentationMode, userNote }
  } catch {
    return { presentationMode: 'daypart', userNote: raw }
  }
}
